import {
  EXAM_KINDS,
  EXAM_TYPES,
  type ContentItem,
  type CreateExamQuestionInput,
  type ExamBody,
  type ExamKind,
  type ExamType,
  type Institution,
  type QuestionBody,
} from '../domain/entities/content.entity';

/**
 * Leitura defensiva do `body` de provas e questões. O back devolve `body` como
 * `unknown` (é `Mixed` no Mongo, polimórfico por tipo), então quem renderiza
 * precisa de um ponto único que valide a forma em vez de espalhar casts.
 */

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : {};
}

/**
 * Campos do `ExamBody`, cada um `null` quando ausente/de tipo errado. Os dois
 * eixos (`examType`, `examKind`) são conferidos contra o enum e não só contra
 * `string`: valor fora da lista não tem rótulo para exibir, então vale tanto
 * quanto ausente.
 */
export function readExamBody(item: ContentItem): {
  year: number | null;
  institutionId: string | null;
  variant: string | null;
  examType: ExamType | null;
  examKind: ExamKind | null;
} {
  const body = asRecord(item.body) as Partial<ExamBody>;
  return {
    year: typeof body.year === 'number' ? body.year : null,
    institutionId:
      typeof body.institutionId === 'string' ? body.institutionId : null,
    variant: typeof body.variant === 'string' ? body.variant : null,
    examType: oneOf(EXAM_TYPES, body.examType),
    examKind: oneOf(EXAM_KINDS, body.examKind),
  };
}

function oneOf<T extends string>(
  allowed: readonly T[],
  raw: unknown,
): T | null {
  return allowed.includes(raw as T) ? (raw as T) : null;
}

/**
 * `null` quando o body não tem a forma de nenhum dos dois formatos válidos.
 * Recebe o `body` cru (não o item) porque serve para os dois lados: o que veio
 * da API e o que o `BodyForm` devolve no submit.
 */
export function readQuestionBody(body: unknown): QuestionBody | null {
  const record = asRecord(body);
  if (typeof record.stem !== 'string') return null;
  if (record.format === 'ESSAY') {
    return {
      format: 'ESSAY',
      stem: record.stem,
      ...(typeof record.expectedAnswer === 'string'
        ? { expectedAnswer: record.expectedAnswer }
        : {}),
      ...readEditorialFlags(record),
    };
  }
  if (record.format !== 'MULTIPLE_CHOICE') return null;
  const alternatives = Array.isArray(record.alternatives)
    ? record.alternatives
        .map(asRecord)
        .filter(
          (alt): alt is { id: string; text: string } =>
            typeof alt.id === 'string' && typeof alt.text === 'string',
        )
    : [];
  return {
    format: 'MULTIPLE_CHOICE',
    stem: record.stem,
    alternatives,
    ...(typeof record.correctAlternativeId === 'string'
      ? { correctAlternativeId: record.correctAlternativeId }
      : {}),
    // Ausente no documento = não anulada (é assim que o back grava: só quando
    // `true`), então `!== true` é a leitura certa, não "campo faltando".
    annulled: record.annulled === true,
    ...readEditorialFlags(record),
  };
}

/**
 * `appealable`/`outdated` de um body cru. Ausente no documento = `false` — o
 * back só grava quando `true`, então `=== true` é a leitura certa e não "campo
 * faltando".
 *
 * Emite as chaves só quando verdadeiras (em vez de sempre, com `false`) para o
 * body remontado aqui ser idêntico ao que o back devolveria — é ele que volta no
 * `build()` do form, e um `outdated: false` a mais faria o `snapshot` do
 * `useFormBridge` acusar sujeira que não existe.
 */
function readEditorialFlags(record: Record<string, unknown>): {
  appealable?: true;
  outdated?: true;
} {
  return {
    ...(record.appealable === true ? { appealable: true as const } : {}),
    ...(record.outdated === true ? { outdated: true as const } : {}),
  };
}

/**
 * `body` do `BodyForm` → campos do payload de criação de questão. São os mesmos
 * nomes, só que planos (o endpoint de questão não recebe um `body` aninhado) —
 * a conversão existe para não espalhar cast de `unknown` pelos componentes.
 */
export function questionBodyToCreateFields(
  body: unknown,
): Pick<
  CreateExamQuestionInput,
  | 'format'
  | 'stem'
  | 'alternatives'
  | 'correctAlternativeId'
  | 'expectedAnswer'
  | 'appealable'
  | 'outdated'
> | null {
  const parsed = readQuestionBody(body);
  if (!parsed) return null;
  // As marcações atravessam: o modal manual usa o mesmo `BodyForm` da edição,
  // então os checkboxes existem lá — e um campo do form que o payload descarta
  // é marcação que some sem aviso na hora de salvar.
  const flags = {
    ...(parsed.appealable === true ? { appealable: true } : {}),
    ...(parsed.outdated === true ? { outdated: true } : {}),
  };
  if (parsed.format === 'ESSAY') {
    return {
      format: 'ESSAY',
      stem: parsed.stem,
      ...(parsed.expectedAnswer !== undefined
        ? { expectedAnswer: parsed.expectedAnswer }
        : {}),
      ...flags,
    };
  }
  return {
    format: 'MULTIPLE_CHOICE',
    stem: parsed.stem,
    alternatives: parsed.alternatives,
    ...(parsed.correctAlternativeId !== undefined
      ? { correctAlternativeId: parsed.correctAlternativeId }
      : {}),
    ...flags,
  };
}

/**
 * `SIGLA-UF` da instituição da prova — forma curta, para célula de tabela.
 * Instituição fora do cache (apagada, ou criada depois do fetch) degrada para o
 * id — nunca esconde a prova.
 */
export function formatInstitution(
  institutionId: string | null,
  byId: ReadonlyMap<string, Institution> | undefined,
): string {
  if (!institutionId) return '—';
  const institution = byId?.get(institutionId);
  if (!institution) return institutionId;
  return `${institution.initials}-${institution.uf}`;
}

/**
 * `(SIGLA-UF) NOME` — **exatamente o formato que o CSV de importação aceita na
 * coluna `institution`** (ver `institution-label.ts` no monolith). Usado onde há
 * espaço para a linha inteira, para que o admin possa copiar daqui e colar na
 * planilha sem ter que lembrar da pontuação.
 */
export function formatInstitutionLabel(
  institutionId: string | null,
  byId: ReadonlyMap<string, Institution> | undefined,
): string {
  if (!institutionId) return '—';
  const institution = byId?.get(institutionId);
  if (!institution) return institutionId;
  return `(${institution.initials}-${institution.uf}) ${institution.name}`;
}
